import React, { useEffect, useState } from 'react'
import './OrderDetail.css';
import { isValidGuid, setSessionStorage } from '../../utils/helper';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store/store';
import Image from '../../components/image/Image';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import Button from '../../components/button/Button';

const STATUS_STEPS = [
  { key: 0,  label: 'รอค่าส่ง' },
  { key: 10, label: 'รอชำระ' },
  { key: 20, label: 'จัดส่ง' },
  { key: 30, label: 'สำเร็จ' },
];

const OrderDetail: React.FC = () => {
  const { messages } = useSignalR();
  const navigate = useNavigate();
  const { GetTransactionAsync } = useApiServices()
  const [detail, setDetail] = useState<any>({})
  const [details, setDetails] = useState<any[]>([])
  const [idArray, setIdArray] = useState<any[]>([])
  const { ids } = useParams();
  const user = useSelector((state: RootState) => state.user)

  const totalAmount = details?.reduce((sum: number, d: any) => {
    const subTotal = d.transactionToProducts?.reduce(
      (s: number, p: any) => s + ((p.price || 0) * (p.quantity || 0)), 0
    ) || 0;
    return sum + subTotal + (d.shippingPrice || 0);
  }, 0);

  const totalShipping = details?.reduce((sum: number, d: any) => sum + (d.shippingPrice || 0), 0);
  const totalProducts = totalAmount - totalShipping;

  useEffect(() => {
    if (!ids) return;
    const idSplit = ids.split(',');
    setIdArray(idSplit);
    if (!idSplit.every(id => isValidGuid(id))) { navigate('/'); return; }

    Promise.all(idSplit.map(id => GetTransactionAsync(id)))
      .then(responses => {
        const validResults = responses
          .filter(x => x.isSuccess)
          .map(x => x.result);
        if (validResults.length > 0) {
          setDetail(validResults[0]);
          setDetails(validResults);
        }
      }).catch(() => navigate('/'));
  }, [ids, navigate]);

  const currentStatus = detail?.currentStatusKey ?? 0;

  return (
    <section className="od-wrapper">
      <div className="od-container lg:container lg:mx-auto">

        {/* Page title */}
        <div className="od-page-header">
          <h2 className="od-page-title">รายละเอียดคำสั่งซื้อ</h2>
          {details.length === 1 && detail?.transactionNumber && (
            <span className="od-order-ref">#{detail.transactionNumber}</span>
          )}
        </div>

        {/* Status stepper — only when single order */}
        {details.length === 1 && (
          <div className="od-status-card">
            <div className="od-stepper">
              {STATUS_STEPS.map((step, i) => {
                const done = currentStatus >= step.key;
                const isLast = i === STATUS_STEPS.length - 1;
                return (
                  <React.Fragment key={step.key}>
                    <div className="od-step">
                      <div className={`od-step-circle ${done ? 'done' : ''}`}>
                        {done ? (
                          <svg viewBox="0 0 12 10" fill="none" className="od-step-check">
                            <path d="M1 5l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <span className="od-step-dot" />
                        )}
                      </div>
                      <span className={`od-step-label ${done ? 'done' : ''}`}>
                        {i == 1 && (currentStatus == 11 || currentStatus == 12) ? "ชำระแล้ว" : step.label}
                      </span>
                    </div>
                    {!isLast && (
                      <div className={`od-step-line ${currentStatus >= STATUS_STEPS[i + 1].key ? 'done' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Recipient info */}
        <div className="od-section-label">ข้อมูลผู้รับ</div>
        <div className="od-card">
          <div className="od-info-row">
            <span className="od-info-key">ชื่อผู้รับ</span>
            <span className="od-info-val">{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</span>
          </div>
          <div className="od-info-row">
            <span className="od-info-key">เบอร์ติดต่อ</span>
            <span className="od-info-val">{user.phone}</span>
          </div>
          {detail?.address && (
            <div className="od-info-row">
              <span className="od-info-key">ที่อยู่</span>
              <span className="od-info-val od-address">
                {[detail.address.addressLine1, detail.address.addressLine2, detail.address.postalCode]
                  .filter(Boolean).join(' ')}
              </span>
            </div>
          )}
        </div>

        {/* Order items — one card per store */}
        <div className="od-section-label">รายการสินค้า</div>
        {details?.map((d: any, i: number) => {
          const storeSubtotal = d.transactionToProducts?.reduce(
            (s: number, p: any) => s + ((p.price || 0) * (p.quantity || 0)), 0
          ) || 0;
          return (
            <div className="od-card od-store-card" key={i}>
              {/* Store header */}
              <div className="od-store-header">
                <img src="/assets/icons/icon-shop.svg" alt="store" className="od-store-icon" onError={(e) => (e.currentTarget.style.display = 'none')} />
                <div className="od-store-meta">
                  <span className="od-store-name">{d.store?.name}</span>
                  <span className="od-store-ref">#{d.transactionNumber}</span>
                </div>
                {details.length > 1 && (
                  <span className={`od-status-badge status-${d.currentStatusKey}`}>
                    {d.transactionStatus?.labelTh || 'รอชำระ'}
                  </span>
                )}
              </div>

              {/* Product rows */}
              <div className="od-product-list">
                {d.transactionToProducts?.map((p: any, j: number) => (
                  <div key={j} className="od-product-row">
                    <div className="od-product-img">
                      <Image id={p.product?.id} alt={p.product?.nameTh} className="w-full h-full object-cover rounded-lg" />
                    </div>
                    <div className="od-product-info">
                      <p className="od-product-name">{p.product?.nameTh}</p>
                      <div className="od-product-meta">
                        <span className="od-product-qty">x{p.quantity}</span>
                        <span className="od-product-price">{(p.price || 0).toLocaleString()} บาท</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Store subtotals */}
              <div className="od-store-totals">
                <div className="od-subtotal-row">
                  <span>ราคาสินค้า</span>
                  <span>{storeSubtotal.toLocaleString()} บาท</span>
                </div>
                {(d.shippingPrice > 0 || currentStatus >= 10) && (
                  <div className="od-subtotal-row">
                    <span>ค่าขนส่ง</span>
                    <span>{d.shippingPrice.toLocaleString()} บาท</span>
                  </div>
                )}
                {(d.shippingPrice === 0 && currentStatus < 10) && (
                  <div className="od-subtotal-row">
                    <span>ค่าขนส่ง</span>
                    <span className="od-shipping-pending">รอการยืนยัน</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Grand total + actions */}
        {details.length > 0 && (
          <div className="od-total-card">
            <div className="od-total-row">
              <span>ราคาสินค้ารวม</span>
              <span>{totalProducts.toLocaleString()} บาท</span>
            </div>
            {totalShipping > 0 && (
              <div className="od-total-row">
                <span>ค่าขนส่งรวม</span>
                <span>{totalShipping.toLocaleString()} บาท</span>
              </div>
            )}
            <div className="od-grand-total-row">
              <span>รวมทั้งหมด</span>
              <span className="od-grand-price">{totalAmount.toLocaleString()} บาท</span>
            </div>
            <div className="od-card-actions">
              <Button variant="secondary" fullWidth onClick={() => navigate('/')}>
                กลับหน้าแรก
              </Button>
              {detail?.currentStatusKey == 10 && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setSessionStorage('i', idArray);
                    setSessionStorage('p', totalAmount);
                    setSessionStorage('r', details.map((x: any) => x.transactionNumber));
                    setSessionStorage('a', detail.address);
                    navigate('/payment');
                  }}
                >
                  ชำระเงิน
                </Button>
              )}
            </div>
          </div>
        )}

      </div>
    </section>
  )
}

export default OrderDetail;
